import * as Phaser from "phaser-ce"
import { COLOR } from "../../../common/Color"
import { log } from "../../../common/Logging"
import { Align, FontType } from "../../../common/TextUtils"
import { StudentStoreSA } from "../../../react/students/StudentStoreSA"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { Blinker } from "../../generic-sprites/Blinker"
import { ButtonContainer, Buttons } from "../../generic-sprites/Buttons"
import { loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { RadioGroup } from "../../generic-sprites/RadioButtons"
import { Line, Rectangle, Shape } from "../../generic-sprites/Shape"
import { Audio } from "../../utils/Audio"
import { ColorUtils } from "../../utils/ColorUtils"
import { PhaserText } from "../../utils/PhaserText"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { Videos } from "../../utils/Videos"
import { Transitions } from "../transitions/Transitions"
import {
    loadAssets,
    PersonalityAssets,
    PersonalityAudio,
    PersonalityImage,
    PersonalityVideo,
} from "./PersonalityAssets"
import { personalityTypeInfoMap } from "./PersonalityInfoMap"
import { personalityQuizData } from "./PersonalityQuizData"
import { PersonalityStage } from "./PersonalityStage"
import { PersonalityStore } from "./PersonalityStore"
import { PersonalityType } from "./PersonalityType"
import { PersonalityDropDownMenu } from "./sprites/PersonalityDropDownMenu"
import { personalityLoadingBar } from "./sprites/PersonalityLoadingBar"

export class PersonalityState extends Phaser.State {
    nextButton: ButtonContainer

    async preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        PersonalityStore.newInstance()
        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setBackgroundColorLightGray(this.game)

        this.intro()
    }

    laptopColor = (backgroundSprite: PersonalityImage) => {
        const quizFrame = this.game.add.group()
        Sprites.add(this.game, backgroundSprite, { group: quizFrame })
    }

    playClickNext = (onComplete: () => void) => {
        const clickNextAudio = this.game.add.audio(PersonalityAudio.CLICK_NEXT)
        clickNextAudio.play()
        clickNextAudio.onStop.addOnce(onComplete)
    }

    intro = async () => {
        const group = this.game.add.group()

        const tempBackground = Sprites.add(this.game, PersonalityImage.OFFICE)

        let username = StudentStoreSA.instance.findUsername()
        if (!username) {
            username = "Unknown"
        }

        const plaqueWidth = Scale.x(0.19)
        const namePlaque = PhaserText.create(
            this.game,
            `${username}\nJunior Reporter`,
            {
                align: Align.CENTER,
                type: FontType.HEADER,
                y: Scale.y(0.401),
            },
        )
        const namePlaqueScaleWidth =
            namePlaque.width / Scale.cachedValues.screenWidth
        if (namePlaqueScaleWidth > 0.15) {
            log.debug("Very wide plaque name, cap width to a maximum")
            namePlaque.width = Scale.x(0.15)
        }
        const namePlaqueRealWidth = namePlaque.width
        const offset = (plaqueWidth - namePlaqueRealWidth) / 2
        namePlaque.x = Scale.x(0.2) + offset

        const intro = await Videos.add(
            this.game,
            PersonalityVideo.INTRO_VIDEO,
            {
                play: false,
                group,
                onComplete: () => {
                    this.computerMenu(PersonalityStage.QUIZ)
                    group.destroy()
                },
            },
        )

        Audio.add(this.game, PersonalityAudio.INTRO, {
            onStop: () => {
                tempBackground.sendToBack()
                Sprites.fadeOut(namePlaque)
                intro.video.play()
            },
        })
    }

    computerMenu = (stage: PersonalityStage) => {
        this.laptopColor(PersonalityImage.LAPTOP_BLUE)

        const group = this.game.add.group()

        PhaserText.create(this.game, "Click City News", {
            type: FontType.NEWS_HEADLINE,
            color: "#FFFFFF",
            fontPx: 36,
            centerInWorldXOnly: true,
            y: Scale.y(0.22),
            style: "Black",
            group,
        })

        PhaserText.create(this.game, "Assignment", {
            type: FontType.HAND_WRITING,
            color: "#FFFFFF",
            fontPx: 30,
            centerInWorldXOnly: true,
            y: Scale.y(0.3),
            group,
        })

        const textMaker = PhaserText.orderedTextList(
            this.game,
            {
                wordWrapWidth: 0.75,
                x: Scale.x(0.12),
                y: Scale.y(0.385),
                color: "#FFFFFF",
                group,
            },
            Scale.y(0.015),
            false,
        )
        const options = [
            textMaker(
                "Take our personality quiz to find out what your personality type is.",
            ),
            textMaker(
                "Use this computer to match yourself to one of the students listed in our computer files.",
            ),
            textMaker(
                "Interview that person to find out what they think about tobacco.",
            ),
            textMaker(
                "Write a short newspaper story summarizing the interview.",
            ),
        ]

        const nextOption = options[stage]
        const box = Shape.makeShapeAround(
            this.game,
            {
                x: nextOption.x - Scale.x(0.04),
                y: nextOption.y,
                width: Scale.x(0.75),
                height: nextOption.height,
            },
            Scale.x(0.01),
            {
                fillAlpha: 0,
                lineColor: 0xffff00,
            },
        )

        const enableMenu = () => {
            Buttons.makeSpriteButton(nextOption, () => {
                group.destroy(true)
                blinker.turnOff()
                switch (stage) {
                    case PersonalityStage.QUIZ: {
                        this.quizStage()
                        break
                    }
                    case PersonalityStage.MATCH: {
                        this.matchStage()
                        break
                    }
                    case PersonalityStage.INTERVIEW: {
                        this.interviewStage()
                        break
                    }
                    case PersonalityStage.STORY: {
                        this.storyStage()
                        break
                    }
                    default:
                        throw new Error("invalid stage")
                }
            })
        }

        const blinker = new Blinker(box.graphics)

        const menuAudioPlayer = () => {
            const playMenuAudio = (menuAudioName: PersonalityAudio) => {
                Audio.add(this.game, menuAudioName, {
                    onStop: () => enableMenu(),
                })
            }
            switch (stage) {
                case PersonalityStage.QUIZ: {
                    playMenuAudio(PersonalityAudio.INTRO_2)
                    break
                }
                case PersonalityStage.MATCH: {
                    playMenuAudio(PersonalityAudio.STEP_2)
                    break
                }
                case PersonalityStage.INTERVIEW: {
                    playMenuAudio(PersonalityAudio.STEP_3)
                    break
                }
                case PersonalityStage.STORY: {
                    playMenuAudio(PersonalityAudio.STEP_4)
                    break
                }
                default:
                    log.debug("invalid stage")
            }
        }
        menuAudioPlayer()
    }

    quizTitle = () => {
        return PhaserText.create(this.game, "Personality Quiz", {
            type: FontType.TITLE,
            color: COLOR.red,
            fontPx: 30,
            x: Scale.x(0.36),
            y: Scale.y(0.2),
            style: "Black",
        })
    }

    quizStage = () => {
        let questionNumber: 1 | 2 | 3 | 4 | 5 = 1

        this.laptopColor(PersonalityImage.LAPTOP_GREEN)
        this.quizTitle()

        const group = this.game.add.group()
        Sprites.add(this.game, PersonalityImage.QUIZ_BACKGROUND, {
            x: Scale.x(0.115),
            y: Scale.y(0.22),
            group,
        })

        // Question Builder
        const questionGroup = this.game.add.group()
        group.add(questionGroup)
        const buildQuestion = () => {
            // wipe slate clean

            PhaserText.create(this.game, `${questionNumber} / 5`, {
                color: COLOR.red,
                fontPx: 18,
                x: Scale.x(0.09),
                y: Scale.y(0.555),
                group: questionGroup,
            })

            // question
            PhaserText.create(
                this.game,
                personalityQuizData[questionNumber - 1].question,
                {
                    type: FontType.HAND_WRITING,
                    color: "#000",
                    fontPx: 30,
                    centerInWorldXOnly: true,
                    y: Scale.y(0.3),
                    wordWrapWidth: 0.7,
                    align: Align.CENTER,
                    group: questionGroup,
                },
            )

            // add radio buttons
            const choices = personalityQuizData[questionNumber - 1].answers.map(
                (item) => ({ text: item[0] }),
            )
            const radioGroup = new RadioGroup(
                this.game,
                Scale.x(0.22),
                Scale.y(0.46),
                choices,
                () => {
                    const letter = String.fromCharCode(
                        "a".charCodeAt(0) + radioGroup.currentlySelectedIndex,
                    )
                    Audio.add(
                        this.game,
                        PersonalityAssets.findQuizAudio(
                            questionNumber,
                            letter as "a" | "b" | "c" | "d" | "e",
                        ),
                        {
                            onStop: () => {
                                this.nextButton.enable(
                                    radioGroup.currentlySelected != null,
                                )
                            },
                        },
                    )
                },
            )
            questionGroup.add(radioGroup.group)

            // Build next question
            this.nextButton = Buttons.create(
                this.game,
                "Next",
                () => {
                    this.nextButton.destroy()
                    PersonalityStore.instance.quizAnswer = {
                        personalityType:
                            personalityQuizData[questionNumber - 1].answers[
                                radioGroup.currentlySelectedIndex
                            ][1],
                        index: questionNumber - 1,
                    }
                    log.debug(PersonalityStore.instance)
                    if (questionNumber >= 5) {
                        group.destroy(true)
                        personalityLoadingBar(
                            this.game,
                            "Please wait while the computer processes your personality profile...",
                            () => this.quizResultsShow(),
                        )
                    } else {
                        questionNumber++
                        radioGroup.destroy()
                        questionGroup.removeAll()
                        this.nextButton.disable()
                        radioGroup.disable()
                        buildQuestion()
                    }
                },
                { x: Scale.x(0.712), y: Scale.y(0.86), group },
            )

            Audio.add(
                this.game,
                PersonalityAssets.findQuizAudio(questionNumber),
                {
                    onPlay: () => {
                        this.nextButton.disable()
                        radioGroup.disable()
                    },
                    onStop: () => {
                        radioGroup.enable()
                    },
                },
            )
        }

        buildQuestion()
    }

    quizResultsShow = () => {
        // tabulate results
        const answers = PersonalityStore.instance.quizAnswers
        const tieBreakerOrder = [
            PersonalityType.KIND_HELPFUL,
            PersonalityType.EXTROVERT,
            PersonalityType.ATHLETIC,
            PersonalityType.RISK_TAKER,
            PersonalityType.SHY,
        ]

        let countedAnswers = answers.reduce((map, current) => {
            map[current] = (map[current] || 0) + 1
            return map
        }, [])
        countedAnswers = [...Object.entries(countedAnswers)]
            .sort((a, b) => b[1] - a[1])
            .reduce((accum, curr) => {
                if (accum[0] === undefined) {
                    accum.push(curr)
                    return accum
                }
                if (accum[0][1] === curr[1]) {
                    accum.push(curr)
                }
                return accum
            }, [])
            .map((item) => item[0])

        log.debug(countedAnswers)
        const result = tieBreakerOrder.find((win) => {
            return countedAnswers.some((val) => val === win)
        })
        log.debug(result)
        PersonalityStore.instance.personalityType = result

        log.debug(PersonalityStore.instance)

        // display results
        const personalityType = PersonalityStore.instance.personalityType
        const group = this.game.add.group()
        Sprites.add(this.game, PersonalityImage.QUIZ_BACKGROUND_2, {
            x: Scale.x(0.115),
            y: Scale.y(0.22),
            group,
        })

        PhaserText.create(
            this.game,
            "Here are the results of your personality quiz:",
            {
                fontPx: 25,
                centerInWorldXOnly: true,
                y: Scale.y(0.28),
                group,
            },
        )

        const typeNumber =
            personalityTypeInfoMap.get(personalityType).typeNumber
        PhaserText.create(
            this.game,
            `Your personality most closely matches \n Personality Type ${typeNumber}`,
            {
                fontPx: 25,
                centerInWorldXOnly: true,
                y: Scale.y(0.34),
                align: Align.CENTER,
                italic: true,
                group,
            },
        )

        const description =
            personalityTypeInfoMap.get(personalityType)[
                StudentStoreSA.instance.currentGender()
            ].description

        PhaserText.create(this.game, description, {
            fontPx: 25,
            centerInWorldXOnly: true,
            y: Scale.y(0.5),
            align: Align.CENTER,
            wordWrapWidth: 0.65,
            group,
        })

        const nextButton = Buttons.create(
            this.game,
            "Next",
            () => {
                group.destroy(true)
                this.quizTitle().destroy()
                this.computerMenu(PersonalityStage.MATCH)
            },
            { x: Scale.x(0.443), y: Scale.y(0.861), group },
        )
        nextButton.disable()

        const resultAudio = this.game.add.audio(PersonalityAudio.QUIZ_RESULTS)
        const resultTypeAudio = this.game.add.audio(
            PersonalityAssets.findMatchResultAudio(personalityType),
        )
        const resultDescriptionAudio = this.game.add.audio(
            PersonalityAssets.findMatchResultAudio(personalityType, true),
        )

        resultAudio.play()
        resultAudio.onStop.addOnce(() => resultTypeAudio.play())

        resultTypeAudio.onStop.addOnce(() => {
            resultDescriptionAudio.play()
        })

        resultDescriptionAudio.onStop.addOnce(() => {
            this.playClickNext(() => nextButton.enable())
        })
    }

    matchStage = () => {
        const personalityType = PersonalityStore.instance.personalityType
        const gender = StudentStoreSA.instance.currentGender()
        this.laptopColor(PersonalityImage.LAPTOP_YELLOW)
        const group = this.game.add.group()
        group.alpha = 0

        personalityLoadingBar(this.game, "Searching...", () => {
            group.alpha = 1
            matchKidAudio.play()
        })

        Sprites.add(this.game, PersonalityImage.MATCH_BACKGROUND_1, {
            x: Scale.x(0.085),
            y: Scale.y(0.188),
            group,
        })

        const kidImage = PersonalityAssets.findKidFace(personalityType, gender)

        Sprites.add(this.game, kidImage, {
            x: Scale.x(0.224),
            y: Scale.y(0.4),
            group,
        })

        const typePersonName =
            personalityTypeInfoMap.get(personalityType)[gender].kidMatch
        PhaserText.create(
            this.game,
            `Your personality match is\n ${typePersonName}`,
            {
                fontPx: 22,
                x: Scale.x(0.45),
                y: Scale.y(0.5),
                align: Align.CENTER,
                group,
            },
        )

        const matchKidAudio = this.game.add.audio(
            PersonalityAssets.findMatchKidAudio(personalityType),
        )
        matchKidAudio.onStop.addOnce(() => {
            this.playClickNext(() => nextButton.enable())
        })

        const nextButton = Buttons.create(
            this.game,
            "Next",
            () => {
                group.destroy(true)
                this.computerMenu(PersonalityStage.INTERVIEW)
            },
            { x: Scale.x(0.45), y: Scale.y(0.77), group },
        )
        nextButton.disable()
    }

    interviewStage = () => {
        const personalityType = PersonalityStore.instance.personalityType
        const gender = StudentStoreSA.instance.currentGender()
        this.laptopColor(PersonalityImage.LAPTOP_YELLOW_RED)

        const group = this.game.add.group()
        Sprites.add(this.game, PersonalityImage.MATCH_BACKGROUND_1, {
            x: Scale.x(0.085),
            y: Scale.y(0.188),
            group,
        })

        const kidImage = PersonalityAssets.findKidFace(personalityType, gender)
        Sprites.add(this.game, kidImage, {
            x: Scale.x(0.71),
            y: Scale.y(0.424),
            group,
        })
        const typePersonName =
            personalityTypeInfoMap.get(personalityType)[gender].kidMatch
        PhaserText.create(
            this.game,
            `Hi my name is ${typePersonName}. You can ask me these questions in any order you like. Click on one to begin:`,
            {
                fontPx: 18,
                centerInWorldXOnly: true,
                y: Scale.y(0.28),
                wordWrapWidth: 0.65,
                group,
            },
        )

        const makeBullet = PhaserText.bulletList(this.game, {
            wordWrapWidth: 0.5,
            fontPx: 18,
            x: Scale.x(0.18),
            y: Scale.y(0.44),
            color: COLOR.red,
            group,
        })

        // Play kid intro
        Audio.add(
            this.game,
            PersonalityAssets.findInterviewKidAudio(personalityType, 0),
            {
                onPlay: () => (group.ignoreChildInput = true),
                onStop: () => (group.ignoreChildInput = false),
            },
        )

        let questionCount = 0
        const questions = [
            "What’s your favorite thing to do?",
            "What are your friends like?",
            "What do you think about vaping or smoking?",
            "Do you think you’ll ever try smoking or vaping?",
            "What do you think about people who vape or smoke?",
        ]
        questions
            .map((question) => makeBullet(question))
            .forEach((question, i) => {
                Buttons.makeSpriteButton(question, () => {
                    Audio.add(
                        this.game,
                        PersonalityAssets.findInterviewQuestionAudio(i),
                        {
                            onPlay: () => (group.ignoreChildInput = true),
                            onStop: () => {
                                kidResponse.play()
                            },
                        },
                    )
                    const kidResponse = this.game.add.audio(
                        PersonalityAssets.findInterviewKidAudio(
                            personalityType,
                            (i + 1) as 1 | 2 | 3 | 4 | 5,
                        ),
                    )
                    kidResponse.onStop.addOnce(() => {
                        group.ignoreChildInput = false
                        question.addColor(COLOR.gray, 0)
                        question.inputEnabled = false
                        questionCount++
                        if (questionCount >= 5) {
                            this.playClickNext(nextButton.enable)
                        }
                    })
                })
            })

        const nextButton = Buttons.create(
            this.game,
            "Next",
            () => {
                group.destroy(true)
                this.computerMenu(PersonalityStage.STORY)
            },
            { x: Scale.x(0.45), y: Scale.y(0.76), group },
        )
        nextButton.disable()
    }

    storyStage = () => {
        const personalityType = PersonalityStore.instance.personalityType
        this.laptopColor(PersonalityImage.LAPTOP_YELLOW)

        Audio.add(this.game, PersonalityAudio.DROPDOWN, {
            onStop: () => (group.ignoreChildInput = false),
        })

        const group = this.game.add.group()
        group.ignoreChildInput = true
        Sprites.add(this.game, PersonalityImage.STORY_BACKGROUND, {
            x: Scale.x(0.085),
            y: Scale.y(0.188),
            group,
        })
        const data =
            personalityTypeInfoMap.get(personalityType)[
                StudentStoreSA.instance.currentGender()
            ]
        const blanks = data.storyBlanks

        const name = data.kidMatch
        const isGirl = StudentStoreSA.instance.isGirl()
        const ZE = isGirl ? "She" : "He"
        const ze = isGirl ? "she" : "he"
        const zer = isGirl ? "her" : "his"

        const makeText = PhaserText.list(
            this.game,
            {
                fontPx: 20,
                wordWrapWidth: 0.75,
                y: Scale.y(0.3),
                x: Scale.x(0.14),
                group,
            },
            Scale.y(0.01),
        )

        const body = [
            `I interviewed ${name}. ${ZE} said one of ${zer} favorite things to do \nis to `,
            `${name} said that ${ze} \n`,
            `${name} said that ${ze} thinks vaping and smoking \n`,
            `${ZE} said ${ze} and ${zer} friends would never use tobacco because \n`,
            `${name} said that ${ze} thinks people who vape or smoke \n`,
        ].map((text) => makeText(text))

        body.forEach((text, i) => {
            new PersonalityDropDownMenu(this.game, blanks[i], {
                x: Scale.x(0.2),
                y: text.cameraOffset.y + text.height,
                placeholder: "(Select an option)",
                blankNum: (i + 1) as 1 | 2 | 3 | 4 | 5,
                personalityType,
                group,
            })
        })

        const titleOptions = [
            "When you use, you lose!",
            "When you vape, you’re in bad shape!",
            "You’re a joke if you smoke!",
            "Tobacco users are just plain losers!",
        ]
        const title = PhaserText.create(this.game, "Title: ", {
            x: Scale.x(0.25),
            y: Scale.y(0.23),
            fontPx: 24,
            group,
        })
        new PersonalityDropDownMenu(this.game, titleOptions, {
            x: Scale.x(0.32),
            y: title.cameraOffset.y + title.height,
            fontPx: 22,
            placeholder: "(Click to add title)",
            isTitleMenu: true,
            group,
        })

        PersonalityStore.instance.storyFinishedButton = Buttons.create(
            this.game,
            "Print",
            () => {
                group.destroy(true)
                this.buildStory()
            },
            { y: Scale.y(0.86), group, centerInWorldX: true },
        )
        PersonalityStore.instance.storyFinishedButton.disable()
    }

    buildStory = () => {
        const gender = StudentStoreSA.instance.currentGender()
        this.laptopColor(PersonalityImage.LAPTOP_YELLOW)

        const personalityType =
            PersonalityStore.instance.personalityType ||
            PersonalityType.KIND_HELPFUL

        const paddingX = Scale.x(0.03)
        const paddingY = Scale.y(0.01)

        const group = this.game.add.group()
        const paper = new Rectangle(
            this.game,
            Scale.x(0.1),
            Scale.y(0.2),
            Scale.x(0.8),
            Scale.y(0.7),
            {
                fillColor: 0xffffff,
                lineWidth: 1,
                group,
            },
        )

        const newsTitle = Sprites.add(this.game, PersonalityImage.NEWS, {
            y: Scale.y(0.22),
        })
        Sprites.centerInWorldXOnly(newsTitle)

        const dateOptions: Intl.DateTimeFormatOptions = {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
        }
        const todaysDate = PhaserText.create(
            this.game,
            new Date().toLocaleDateString("en-US", dateOptions),
            {
                fontPx: 14,
                type: FontType.NEWS_HEADLINE,
                group,
            },
        )
        todaysDate.alignIn(
            paper.invisibleSprite,
            Phaser.TOP_LEFT,
            -paddingX,
            Scale.y(-0.14),
        )
        const vol = PhaserText.create(this.game, "Vol. 1, Issue 54", {
            fontPx: 14,
            type: FontType.NEWS_HEADLINE,
            align: Align.RIGHT,
            group,
        })
        vol.alignIn(
            paper.invisibleSprite,
            Phaser.TOP_RIGHT,
            -paddingX,
            Scale.y(-0.14),
        )

        // topLine
        new Line(
            this.game,
            todaysDate.x,
            todaysDate.y - Scale.y(0.005),
            vol.x,
            vol.y - Scale.y(0.005),
            { group },
        )
        // bottomLine
        new Line(
            this.game,
            todaysDate.x,
            todaysDate.y + todaysDate.height,
            vol.x,
            vol.y + vol.height,
            { group },
        )

        // articleTitle
        PhaserText.create(
            this.game,
            PersonalityStore.instance.articleTitle || "Title not Set",
            {
                align: Align.CENTER,
                centerInWorldXOnly: true,
                y: Scale.y(0.39),
                type: FontType.NEWS_HEADLINE,
                group,
            },
        )

        const kidImageFile = PersonalityAssets.findKidFace(
            personalityType,
            gender,
        )
        const kidImg = Sprites.add(this.game, kidImageFile, {
            x: paper.invisibleSprite.x + paddingX,
            y: Scale.y(0.45),
            group,
        })

        const kidName = PhaserText.create(
            this.game,
            personalityTypeInfoMap.get(personalityType)[gender].kidMatch,
            {
                align: Align.CENTER,
                x: kidImg.x,
                y: kidImg.y + kidImg.height + paddingX,
                type: FontType.NEWSPAPER,
                fontPx: 14,
                group,
            },
        )

        kidName.alignTo(kidImg, Phaser.BOTTOM_CENTER, 0, paddingY)

        // titleLine
        new Line(
            this.game,
            kidImg.x + kidImg.width + paddingX,
            kidImg.y,
            kidImg.x + kidImg.width + paddingX,
            kidImg.height,
        )

        let username = StudentStoreSA.instance.findUsername()
        if (!username) {
            username = "Unknown"
        }
        const studentName = PhaserText.create(this.game, `By: ${username}`, {
            x: kidImg.x + kidImg.width + paddingX,
            y: kidImg.y + paddingY,
            fontPx: 12,
            italic: true,
            type: FontType.NEWSPAPER,
            group,
        })

        const name =
            personalityTypeInfoMap.get(personalityType)[gender].kidMatch
        const isGirl = StudentStoreSA.instance.isGirl()
        const ZE = isGirl ? "She" : "He"
        const ze = isGirl ? "she" : "he"
        const zer = isGirl ? "her" : "his"

        const blanks = PersonalityStore.instance.blanks

        const body =
            `I interviewed ${name}. ${ZE} said one of ${zer} favorite things to do is to ${blanks[0]}. ${name} ` +
            `said that ${ze} ${blanks[1]}. ${name} ` +
            `said that ${ze} thinks` +
            ` vaping and smoking ${blanks[2]}. ${ZE} said ${ze} and ${zer} friends would never use ` +
            `tobacco because ${blanks[3]}. ${name} said that ${ze} thinks people who vape or smoke ${blanks[4]}.`
        // articleBody
        PhaserText.create(this.game, body, {
            fontPx: 16,
            type: FontType.NEWSPAPER,
            wordWrapWidth: 0.5,
            x: kidImg.x + kidImg.width + paddingX,
            y: studentName.y + studentName.height + paddingY,
            group,
        })

        const nextButton = Buttons.create(
            this.game,
            "Next",
            () => {
                group.destroy(true)
                Transitions.startOutro(this.game, ActivityKey.PERSONALITY)
            },
            { x: Scale.x(0.75), y: Scale.y(0.83), group },
        )
        nextButton.disable()

        Audio.add(this.game, PersonalityAudio.OUTRO, {
            onStop: nextButton.enable,
        })
    }
}
